import { FormApi } from "final-form";
import _ from "lodash";
import { Form, useForm, useFormState } from "react-final-form";
import { useTranslation } from "react-i18next";
import { shallowEqual } from "react-redux";
import { useSelector } from "src/store";
import { capitalizeFirstLetter } from "src/ui/utils/string";

import { selectProvisionings } from "@dashboard/provisioning/store/selectors";
import { Provisioning, SystemSetting } from "@dashboard/provisioning/types";
import { Row, SectionDescription } from "@dashboard/shared/styles";
import { Typography, useTheme } from "@mui/material";
import { DangerOutlinedButton, PrimaryOutlinedButton } from "@shared/CustomButton";
import { ControlledSelectField } from "@shared/form/SelectField/SelectField";
import { TextField } from "@shared/form/TextField";
import { HeaderLabel } from "@shared/table/HeaderLabel";
import {
    MinimalTable,
    MinimalTableBody,
    MinimalTableCell,
    MinimalTableContainer,
    MinimalTableHeader,
    MinimalTableHeaderCell,
    MinimalTableRow,
} from "@shared/table/MinimalTableComponents";

import {
    CollapsibleProvider,
    CollapsibleSectionDetails,
    CollapsibleSectionSummary,
    CollapsibleSectionTitle,
    useCollapsiblePersistence,
} from "../../shared/components/CollapsibleSection";
import { optionsSystemSettingSections, optionsType } from "../utils";

const initialValues: Partial<SystemSetting> = {
    key: undefined,
    value: undefined,
    section: "Global",
    type: "Bool",
    comment: null,
};

type FormValues = SystemSetting;
type ValidationErros = Omit<FormValues, "section" | "type"> & { section: string; type: string };

export const SystemSettings = () => {
    const { value: initialValue, onChange } = useCollapsiblePersistence("provisioningPage.collapsedSections", "SystemSettings");
    const theme = useTheme();
    const parentForm = useForm();
    const { t } = useTranslation(["pages", "common"]);

    const provisionings = useSelector(selectProvisionings, shallowEqual);
    const { values: parentValues } = useFormState<Partial<Provisioning>>();
    const systemSettings = parentValues?.settings?.systemSettings || [];
    const summary = systemSettings.length
        ? t("pages:provisioningPage.section.systemSettings.summary", { count: systemSettings.length })
        : undefined;

    const onSubmit = (values: FormValues, form: FormApi<FormValues>) => {
        const newValue = { ...values, comment: values.comment || null };
        if (systemSettings.length) {
            parentForm.change("settings.systemSettings", [...systemSettings, newValue]);
        }
        if (!systemSettings.length) {
            parentForm.change("settings.systemSettings", [newValue]);
        }
        form.restart(initialValues);
    };
    const validate = (values: FormValues) => {
        return _.transform<FormValues, ValidationErros>(values, (result, value, key) => {
            if (!value && key !== "comment") {
                result[key] = t("pages:provisioningPage.input.settingComment.validation.empty", {
                    component: capitalizeFirstLetter(key),
                });
            }
        });
    };
    const handleDelete = (index: number) => {
        parentForm.change(
            "settings.systemSettings",
            systemSettings.filter((_item, i) => i !== index),
        );
    };

    return (
        <Form<FormValues>
            validate={validate}
            onSubmit={onSubmit}
            initialValues={initialValues}
            render={({ form }) => (
                <CollapsibleProvider initialValue={initialValue} onChange={onChange}>
                    <CollapsibleSectionSummary>
                        <CollapsibleSectionTitle summary={summary}>
                            {t("pages:provisioningPage.section.systemSettings.title")}
                        </CollapsibleSectionTitle>
                    </CollapsibleSectionSummary>
                    <CollapsibleSectionDetails>
                        <SectionDescription>{t("pages:provisioningPage.section.systemSettings.description")}</SectionDescription>
                        <MinimalTableContainer>
                            <MinimalTable>
                                <MinimalTableHeader>
                                    <MinimalTableRow>
                                        <MinimalTableHeaderCell align="left">
                                            <Typography fontWeight="bold">
                                                <HeaderLabel tooltip={t("pages:provisioningPage.table.headerTooltip.section")}>
                                                    {t("pages:provisioningPage.table.header.section")}
                                                </HeaderLabel>
                                            </Typography>
                                        </MinimalTableHeaderCell>
                                        <MinimalTableHeaderCell align="left">
                                            <Typography fontWeight="bold">
                                                {t("pages:provisioningPage.table.header.type")}
                                            </Typography>
                                        </MinimalTableHeaderCell>
                                        <MinimalTableHeaderCell align="left">
                                            <Typography fontWeight="bold">{t("common:label.key")}</Typography>
                                        </MinimalTableHeaderCell>
                                        <MinimalTableHeaderCell align="left">
                                            <Typography fontWeight="bold">{t("common:label.value")}</Typography>
                                        </MinimalTableHeaderCell>
                                        <MinimalTableHeaderCell align="left">
                                            <Typography fontWeight="bold">{t("common:label.comment")}</Typography>
                                        </MinimalTableHeaderCell>
                                        <MinimalTableHeaderCell align="right"></MinimalTableHeaderCell>
                                    </MinimalTableRow>
                                </MinimalTableHeader>
                                <MinimalTableBody>
                                    {systemSettings.length ? (
                                        systemSettings.map((item, index) => (
                                            <MinimalTableRow key={item.key + item.value}>
                                                <MinimalTableCell component="th" scope="row" align="left">
                                                    <Typography>{item.section}</Typography>
                                                </MinimalTableCell>
                                                <MinimalTableCell align="left">
                                                    <Typography>{item.type}</Typography>
                                                </MinimalTableCell>
                                                <MinimalTableCell align="left">
                                                    <Typography>{item.key}</Typography>
                                                </MinimalTableCell>
                                                <MinimalTableCell align="left">
                                                    <Typography>{item.value}</Typography>
                                                </MinimalTableCell>
                                                <MinimalTableCell align="left">
                                                    <Typography>{item.comment}</Typography>
                                                </MinimalTableCell>
                                                <MinimalTableCell align="right">
                                                    <DangerOutlinedButton
                                                        onClick={() => handleDelete(index)}
                                                        tooltipProps={{ title: t("common:button.delete") }}
                                                        name="system-setting-delete"
                                                    >
                                                        <i className="fa-solid fa-trash" />
                                                    </DangerOutlinedButton>
                                                </MinimalTableCell>
                                            </MinimalTableRow>
                                        ))
                                    ) : (
                                        <MinimalTableRow>
                                            <MinimalTableCell align="center" colSpan={6}>
                                                <Typography>{t("pages:provisioningPage.table.noSystemSettings")}</Typography>
                                            </MinimalTableCell>
                                        </MinimalTableRow>
                                    )}
                                </MinimalTableBody>
                            </MinimalTable>
                        </MinimalTableContainer>
                        <Row style={{ paddingBottom: theme.spacing(3), marginTop: theme.spacing(4) }}>
                            <ControlledSelectField
                                name="section"
                                label={t("pages:provisioningPage.input.section.caption")}
                                options={optionsSystemSettingSections}
                                disabled={!provisionings.length}
                                fullWidth
                            />
                            <ControlledSelectField
                                name="type"
                                label={t("pages:provisioningPage.input.settingType.caption")}
                                options={optionsType.map((item) => ({ label: item, value: item }))}
                                disabled={!provisionings.length}
                                fullWidth
                            />
                        </Row>
                        <Row style={{ paddingBottom: theme.spacing(3) }}>
                            <TextField
                                name="key"
                                label={t("pages:provisioningPage.input.settingKey.caption")}
                                disabled={!provisionings.length}
                                fullWidth
                            />
                            <TextField
                                name="value"
                                label={t("pages:provisioningPage.input.settingValue.caption")}
                                disabled={!provisionings.length}
                                fullWidth
                            />
                        </Row>
                        <Row>
                            <TextField
                                name="comment"
                                label={t("common:label.comment")}
                                disabled={!provisionings.length}
                                fullWidth
                            />
                            <PrimaryOutlinedButton
                                onClick={form.submit}
                                disabled={!provisionings.length}
                                name="system-setting-add"
                            >
                                {t("common:button.add")}
                            </PrimaryOutlinedButton>
                        </Row>
                    </CollapsibleSectionDetails>
                </CollapsibleProvider>
            )}
        />
    );
};
